/**
 * NudgeTemplatesCard — workspace admin surface for 3-5 canned WhatsApp/SMS
 * replies. Sellers open this once, write their nudges (e.g., "Free shipping
 * today only", "5% off next hour"), and can then fire them:
 *   1. From WhatsApp by replying `n1`/`n2`/`n3` to any Twilio alert.
 *   2. From the workspace UI via the compact "Send" buttons here.
 *
 * Mounted at the top of /workspace/intent-radar just below PushAlertsCard.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Save, Send, MessageCircle, Sparkles, RotateCcw, Zap, KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";

interface Template {
  id: number;
  title: string;
  body: string;
  use_count: number;
  created_at?: string;
  updated_at?: string;
  last_used_at?: string;
}

interface TemplatesPayload {
  templates: Template[];
  max: number;
  max_title: number;
  max_body: number;
}

interface HistoryRow {
  buyer_user_id: string | null;
  to_phone: string;
  channel: string;
  template_id: number | null;
  template_title: string | null;
  body_preview: string;
  status: "delivered" | "failed";
  twilio_error?: string | null;
  created_at: string;
}

const EMPTY_ROWS = [
  { id: 1, title: "", body: "" },
  { id: 2, title: "", body: "" },
  { id: 3, title: "", body: "" },
  { id: 4, title: "", body: "" },
  { id: 5, title: "", body: "" },
];

export const NudgeTemplatesCard: React.FC = () => {
  const qc = useQueryClient();
  const q = useQuery<TemplatesPayload>({
    queryKey: ["nudge-templates"],
    queryFn: async () => (await api.get("/nudges/templates")).data,
  });
  const history = useQuery<{ sends: HistoryRow[] }>({
    queryKey: ["nudge-history"],
    queryFn: async () => (await api.get("/nudges/history?limit=6")).data,
    refetchInterval: 60_000,
  });

  const [draft, setDraft] = useState<Template[]>(EMPTY_ROWS.map(r => ({ ...r, use_count: 0 })));

  useEffect(() => {
    if (!q.data) return;
    const seeded = [...q.data.templates];
    while (seeded.length < 5) {
      seeded.push({ id: seeded.length + 1, title: "", body: "", use_count: 0 });
    }
    setDraft(seeded);
  }, [q.data]);

  const dirty = useMemo(() => {
    if (!q.data) return false;
    const original = q.data.templates;
    // Non-empty rows that differ from the persisted set.
    return draft.some(t => {
      const p = original.find(o => o.id === t.id);
      const wasEmpty = !p;
      const isEmpty = !t.title.trim() && !t.body.trim();
      if (wasEmpty && isEmpty) return false;
      if (wasEmpty) return true;
      return (p!.title !== t.title.trim()) || (p!.body !== t.body.trim());
    });
  }, [draft, q.data]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = draft
        .filter(t => t.title.trim() && t.body.trim())
        .map(t => ({ id: t.id, title: t.title.trim(), body: t.body.trim() }));
      return (await api.put("/nudges/templates", { templates: payload })).data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["nudge-templates"], data);
      toast.success("Templates saved.");
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  function updateRow(id: number, patch: Partial<Template>) {
    setDraft(rows => rows.map(r => r.id === id ? { ...r, ...patch } : r));
  }
  function resetRow(id: number) {
    updateRow(id, { title: "", body: "" });
  }

  if (q.isLoading) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 mb-4" data-testid="nudge-templates-card">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    );
  }
  const max = q.data?.max || 5;
  const maxBody = q.data?.max_body || 500;

  return (
    <div
      className="rounded-2xl border border-neutral-200 bg-white overflow-hidden mb-4"
      data-testid="nudge-templates-card"
    >
      <div className="px-5 py-4 border-b border-neutral-100 bg-gradient-to-r from-fuchsia-50 to-orange-50 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-orange-500 grid place-items-center text-white shrink-0 shadow-sm">
            <Zap className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-neutral-500">
              Buyer Nudge Templates
            </div>
            <div className="text-sm text-neutral-700">
              Save up to {max} canned WhatsApp / SMS replies. Fire them at a hot buyer in one tap —
              or from WhatsApp itself by replying{" "}
              <code className="font-mono bg-white border border-fuchsia-200 rounded px-1">n1</code>{" "}/{" "}
              <code className="font-mono bg-white border border-fuchsia-200 rounded px-1">n2</code>{" "}/{" "}
              <code className="font-mono bg-white border border-fuchsia-200 rounded px-1">n3</code>{" "}
              to any alert.
            </div>
          </div>
        </div>
        <Button
          data-testid="nudge-save-btn"
          size="sm"
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
          className="bg-neutral-900 text-white hover:bg-black"
        >
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
          Save templates
        </Button>
      </div>

      <div className="p-5 grid md:grid-cols-2 gap-4">
        {draft.map((t) => (
          <div
            key={t.id}
            data-testid={`nudge-row-${t.id}`}
            className="rounded-xl border border-neutral-200 bg-neutral-50 p-3"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-white border border-fuchsia-200 px-2 py-0.5 text-[10px] font-bold text-fuchsia-700 uppercase tracking-widest">
                <KeyRound className="h-2.5 w-2.5" /> n{t.id}
              </span>
              {t.use_count > 0 && (
                <span className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5">
                  {t.use_count} sent
                </span>
              )}
              <div className="flex-1" />
              {(t.title || t.body) && (
                <button
                  data-testid={`nudge-clear-${t.id}`}
                  onClick={() => resetRow(t.id)}
                  aria-label="Clear this row"
                  className="text-neutral-400 hover:text-neutral-800 p-1 rounded"
                >
                  <RotateCcw className="h-3 w-3" />
                </button>
              )}
            </div>
            <input
              data-testid={`nudge-title-${t.id}`}
              value={t.title}
              onChange={(e) => updateRow(t.id, { title: e.target.value.slice(0, 32) })}
              placeholder={`Slot ${t.id} title (e.g. Free shipping today)`}
              className="w-full px-2 py-1.5 rounded-md text-sm bg-white border border-neutral-200 focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500/15 outline-none font-semibold mb-1.5"
            />
            <textarea
              data-testid={`nudge-body-${t.id}`}
              value={t.body}
              onChange={(e) => updateRow(t.id, { body: e.target.value.slice(0, maxBody) })}
              rows={3}
              placeholder="What Sancta should send this buyer…"
              className="w-full px-2 py-1.5 rounded-md text-sm bg-white border border-neutral-200 focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500/15 outline-none resize-none"
            />
            <div className="mt-1 flex items-center justify-between text-[10px] text-neutral-400">
              <span className="inline-flex items-center gap-1">
                <MessageCircle className="h-3 w-3" />
                WhatsApp / SMS
              </span>
              <span className="tabular-nums font-mono">
                {t.body.length}/{maxBody}
              </span>
            </div>
          </div>
        ))}
      </div>

      {(history.data?.sends?.length ?? 0) > 0 && (
        <div
          className="px-5 py-4 border-t border-neutral-100 bg-neutral-50"
          data-testid="nudge-history"
        >
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-2 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Recent nudges sent
          </div>
          <div className="space-y-1.5">
            {history.data!.sends.slice(0, 5).map((s, i) => (
              <div
                key={i}
                data-testid={`nudge-history-row-${i}`}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-xs bg-white border ${
                  s.status === "delivered" ? "border-emerald-200" : "border-rose-200"
                }`}
              >
                <span className={`h-2 w-2 rounded-full ${s.status === "delivered" ? "bg-emerald-500" : "bg-rose-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-neutral-800 truncate">
                    {s.template_title ? `n${s.template_id} · ${s.template_title}` : "Custom message"}
                  </div>
                  <div className="text-[11px] text-neutral-500 truncate">
                    → {s.to_phone} · {s.channel}
                    {s.twilio_error ? ` · ${s.twilio_error}` : ""}
                  </div>
                </div>
                <span className="text-[10px] text-neutral-400 font-mono shrink-0">
                  {new Date(s.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NudgeTemplatesCard;
